import { useState, useCallback, useEffect } from 'react'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers'
import { Box, Button, Typography, Grid, Stack, ToggleButton } from '@mui/material'
import { dayOfYearToDate, dateToDayOfYear, isValidFrostDateRange, formatDOYAsDate } from '../../common/utils/doy-utils'
import { Restore } from '@mui/icons-material'
import { TpTooltip, TpToggleButtonGroup } from '~/components'
import _ from 'lodash'

const MAX_WIDTH_DATE_PICKER_PX = 350

const SIGMA_MAP = { '50': 0, '84': 1, '97': 2 }

const PERCENTILE_DESCRIPTIONS = {
  '50': 'Half of all seasons will see a frost after the spring date or before the fall date shown (50th percentile).',
  '84': 'About 5 in 6 seasons will be frost-free within this window (1 standard deviation / 84th percentile).',
  '97': 'About 39 in 40 seasons will be frost-free within this window (2 standard deviations / 97.5th percentile).',
}

const frostPickerProps = {
  textField: {
    size: 'small',
    InputLabelProps: {
      sx: {
        color: '#1976d2',
        fontWeight: 600,
        '&.Mui-focused': {
          color: '#1565c0',
        },
      },
    },
    InputProps: {
      sx: {
        '& .MuiPickersOutlinedInput-notchedOutline': {
          borderColor: '#1976d2',
        },
        '&:hover .MuiPickersOutlinedInput-notchedOutline': {
          borderColor: '#1976d2',
        },
        '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
          borderColor: '#1565c0',
          boxShadow: '0 0 0 1.5px #1976d2',
        },
      },
    },
  }
}

export const FrostDateEditor = ({
  defaultLfd,
  defaultFfd,
  stdLfd,
  stdFfd,
  onLfdChange,
  onFfdChange,
  onReset,
  onModifiedChange,
}) => {
  const computeAdjusted = useCallback((mult) => ({
    lfd: Math.round(defaultLfd + mult * stdLfd),
    ffd: Math.round(defaultFfd - mult * stdFfd),
  }), [defaultLfd, defaultFfd, stdLfd, stdFfd])

  const [percentile, setPercentile] = useState('84')
  const [lfdDate, setLfdDate] = useState(() => {
    if (defaultLfd && stdLfd) return dayOfYearToDate(Math.round(defaultLfd + stdLfd))
    if (defaultLfd) return dayOfYearToDate(defaultLfd)
    return null
  })
  const [ffdDate, setFfdDate] = useState(() => {
    if (defaultFfd && stdFfd) return dayOfYearToDate(Math.round(defaultFfd - stdFfd))
    if (defaultFfd) return dayOfYearToDate(defaultFfd)
    return null
  })
  const [isModified, setIsModified] = useState(false)
  const [error, setError] = useState(null)

  // Notify parent of initial 84th-percentile values on mount
  useEffect(() => {
    if (!defaultLfd || !defaultFfd) return
    const { lfd, ffd } = computeAdjusted(1)
    onLfdChange(lfd)
    onFfdChange(ffd)
    onModifiedChange?.(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePercentileChange = useCallback((_, newVal) => {
    if (!newVal) return
    setPercentile(newVal)
    const mult = SIGMA_MAP[newVal]
    const { lfd, ffd } = computeAdjusted(mult)
    setLfdDate(dayOfYearToDate(lfd))
    setFfdDate(dayOfYearToDate(ffd))
    setIsModified(false)
    setError(null)
    onLfdChange(lfd)
    onFfdChange(ffd)
    onModifiedChange?.(false)
  }, [computeAdjusted, onLfdChange, onFfdChange, onModifiedChange])

  const handleLfdChange = useCallback((newDate) => {
    if (!newDate) return
    setLfdDate(newDate)
    setIsModified(true)
    onModifiedChange?.(true)
    const newLfd = dateToDayOfYear(newDate)
    const currentFfd = ffdDate ? dateToDayOfYear(ffdDate) : null
    if (currentFfd && !isValidFrostDateRange(newLfd, currentFfd)) {
      setError('Last frost date must be before first frost date')
      return
    }
    setError(null)
    onLfdChange(newLfd)
  }, [ffdDate, onLfdChange, onModifiedChange])

  const handleFfdChange = useCallback((newDate) => {
    if (!newDate) return
    setFfdDate(newDate)
    setIsModified(true)
    onModifiedChange?.(true)
    const currentLfd = lfdDate ? dateToDayOfYear(lfdDate) : null
    const newFfd = dateToDayOfYear(newDate)
    if (currentLfd && !isValidFrostDateRange(currentLfd, newFfd)) {
      setError('First frost date must be after last frost date')
      return
    }
    setError(null)
    onFfdChange(newFfd)
  }, [lfdDate, onFfdChange, onModifiedChange])

  const handleReset = useCallback(() => {
    const mult = SIGMA_MAP[percentile]
    const { lfd, ffd } = computeAdjusted(mult)
    setLfdDate(dayOfYearToDate(lfd))
    setFfdDate(dayOfYearToDate(ffd))
    setIsModified(false)
    setError(null)
    onLfdChange(lfd)
    onFfdChange(ffd)
    onModifiedChange?.(false)
    onReset()
  }, [percentile, computeAdjusted, onLfdChange, onFfdChange, onModifiedChange, onReset])

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>

        {/* Title */}
        <TpTooltip title='You will be able to report actual frost dates later, these are just estimates for planning crop schedules'>
          <Typography variant='h6' sx={{ fontWeight: 'bold', pb: 1 }}>
            Estimated Frost Dates*
          </Typography>
        </TpTooltip>

        {/* Climate stats: mean + variability */}
        <Grid container spacing={1} sx={{ pb: 1 }}>
          <Grid size={{ xs: 6 }}>
            <Typography variant='caption' color='text.secondary' component='div' sx={{ lineHeight: 1.6 }}>
              Last spring frost <strong>mean</strong>
            </Typography>
            <Typography variant='body2'>
              {defaultLfd ? formatDOYAsDate(defaultLfd) : '—'}
              {defaultLfd && (
                <Typography component='span' variant='caption' color='text.secondary' sx={{ ml: 0.5 }}>
                  (day {defaultLfd})
                </Typography>
              )}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Year-to-year variability: <strong>±{_.round(stdLfd)} days</strong>
            </Typography>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography variant='caption' color='text.secondary' component='div' sx={{ lineHeight: 1.6 }}>
              First fall frost <strong>mean</strong>
            </Typography>
            <Typography variant='body2'>
              {defaultFfd ? formatDOYAsDate(defaultFfd) : '—'}
              {defaultFfd && (
                <Typography component='span' variant='caption' color='text.secondary' sx={{ ml: 0.5 }}>
                  (day {defaultFfd})
                </Typography>
              )}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Year-to-year variability: <strong>±{_.round(stdFfd)} days</strong>
            </Typography>
          </Grid>
        </Grid>

        {/* Top row: toggle + reset */}
        <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between' sx={{ pb: 1 }}>
          <TpToggleButtonGroup
            value={percentile}
            exclusive
            onChange={handlePercentileChange}
            size='small'
          >
            <ToggleButton value='50'>
              Aggressive
              <Box component='span' sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.5 }}>(50th)</Box>
            </ToggleButton>
            <ToggleButton value='84'>
              Normal
              <Box component='span' sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.5 }}>(84th)</Box>
            </ToggleButton>
            <ToggleButton value='97'>
              Safe
              <Box component='span' sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.5 }}>(97.5th)</Box>
            </ToggleButton>
          </TpToggleButtonGroup>
          {isModified && (
            <Button
              variant='text'
              size='small'
              onClick={handleReset}
              startIcon={<Restore />}
            >
              Reset
            </Button>
          )}
        </Stack>

        {/* Percentile description */}
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', pb: 1.5, fontStyle: 'italic' }}>
          {PERCENTILE_DESCRIPTIONS[percentile]}
        </Typography>

        {/* Date pickers */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 12 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '3fr 2fr' }, alignItems: 'center', gap: 2, rowGap: 0.5, maxWidth: MAX_WIDTH_DATE_PICKER_PX }}>
              <DatePicker
                label="Last Spring Frost Date"
                views={['month', 'day']}
                value={lfdDate}
                onChange={handleLfdChange}
                format="MMM D"
                sx={{ flex: 1, minWidth: 30 }}
                slotProps={frostPickerProps}
              />
        
              {/* {lfdDate && (
                <Typography textAlign='center' variant='caption' color='text.secondary' sx={{ lineHeight: 1.25 }}>
                  DOY <strong>{dateToDayOfYear(lfdDate)}</strong>
                </Typography>
              )} */}
            </Box>
          </Grid>
          <Grid size={{ xs: 6, md: 12 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '3fr 2fr' }, alignItems: 'center', gap: 2, rowGap: 0.5, maxWidth: MAX_WIDTH_DATE_PICKER_PX }}>
              <DatePicker
                label='First Fall Frost Date'
                views={['month', 'day']}
                value={ffdDate}
                onChange={handleFfdChange}
                sx={{ flex: 1, minWidth: 30 }}
                format='MMM D'
                slotProps={frostPickerProps}
              />
              {/* {ffdDate && (
                <Typography textAlign='center' variant='caption' color='text.secondary' sx={{ lineHeight: 1.25 }}>
                  DOY <strong>{dateToDayOfYear(ffdDate)}</strong>
                </Typography>
              )} */}
            </Box>
          </Grid>
        </Grid>

        {error && (
          <Typography variant='body2' color='error' sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </Box>
    </LocalizationProvider>
  )
}
